import { useEffect, useRef, useState, useCallback } from 'react'
import { Loader2, Check, X } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { WORKER_URL } from '../lib/env'

const RESEND_COOLDOWN = 60 // seconds
const CODE_LENGTH = 6

interface Props {
  email: string
  /** Called once verification succeeds. Parent should refresh brand state. */
  onVerified: () => void
  /** Optional sign-out for "wrong email" escape hatch. */
  onSignOut?: () => void
  /** When true, fire send-verification immediately on open (signin flow). */
  autoSendOnOpen?: boolean
}

/**
 * 6-digit OTP modal. Sends a fresh code on mount when autoSendOnOpen is true,
 * otherwise the parent (signup flow) is expected to have already triggered
 * the send via the same /api/brand/send-verification endpoint.
 *
 * Worker contract:
 *   POST /api/brand/send-verification → { status: 'sent' | 'already_verified' } | 429 retry_after
 *   POST /api/brand/verify-email     → { status: 'verified' } | 400 { error, attempts_remaining? }
 */
export default function EmailVerificationModal({ email, onVerified, onSignOut, autoSendOnOpen = false }: Props) {
  const { getToken } = useAuth()
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''))
  const [submitting, setSubmitting] = useState(false)
  const [verified, setVerified] = useState(false)
  const [error, setError] = useState<string>('')
  const [shake, setShake] = useState(false)
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [resendError, setResendError] = useState('')
  const [cooldown, setCooldown] = useState(0)
  const inputsRef = useRef<Array<HTMLInputElement | null>>([])
  const deadlineRef = useRef(0)
  const sentOnMountRef = useRef(false)

  const recomputeCooldown = useCallback(() => {
    const remaining = Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000))
    setCooldown(remaining)
  }, [])

  const startCooldown = useCallback((seconds: number) => {
    deadlineRef.current = Date.now() + seconds * 1000
    setCooldown(seconds)
  }, [])

  const sendCode = useCallback(async (silent = false): Promise<void> => {
    if (!silent) {
      setResendStatus('sending')
      setResendError('')
    }
    try {
      const token = await getToken()
      const res = await fetch(`${WORKER_URL}/api/brand/send-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token || ''}` },
      })
      if (res.status === 429) {
        const body = await res.json().catch(() => ({})) as { retry_after?: number }
        const wait = body.retry_after || RESEND_COOLDOWN
        if (!silent) {
          setResendError(`Wait ${wait}s before requesting another code.`)
          setResendStatus('error')
        }
        startCooldown(wait)
        return
      }
      if (!res.ok) {
        if (!silent) {
          setResendError('Could not send code. Please try again.')
          setResendStatus('error')
        }
        return
      }
      const body = await res.json().catch(() => ({})) as { status?: string }
      if (body.status === 'already_verified') {
        setVerified(true)
        setTimeout(onVerified, 600)
        return
      }
      if (!silent) {
        setResendStatus('sent')
        setTimeout(() => setResendStatus('idle'), 2500)
      }
      startCooldown(RESEND_COOLDOWN)
    } catch {
      if (!silent) {
        setResendError('Network error. Check your connection.')
        setResendStatus('error')
      }
    }
  }, [getToken, onVerified, startCooldown])

  // Auto-send on mount if requested (signin path). For signup path the caller
  // already fired the send before opening the modal, so we just start the
  // resend cooldown without re-sending.
  useEffect(() => {
    if (sentOnMountRef.current) return
    sentOnMountRef.current = true
    if (autoSendOnOpen) {
      void sendCode(true)
    } else {
      startCooldown(RESEND_COOLDOWN)
    }
    // Focus first input on mount
    setTimeout(() => inputsRef.current[0]?.focus(), 50)
  }, [autoSendOnOpen, sendCode, startCooldown])

  // Cooldown ticker
  useEffect(() => {
    if (cooldown <= 0) return
    const t = setInterval(recomputeCooldown, 1000)
    return () => clearInterval(t)
  }, [cooldown, recomputeCooldown])

  const submit = useCallback(async (code: string) => {
    if (submitting || verified) return
    setSubmitting(true)
    setError('')
    try {
      const token = await getToken()
      const res = await fetch(`${WORKER_URL}/api/brand/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token || ''}` },
        body: JSON.stringify({ code }),
      })
      const body = await res.json().catch(() => ({})) as { status?: string; error?: string; attempts_remaining?: number }
      if (res.ok && body.status === 'verified') {
        setVerified(true)
        setTimeout(onVerified, 700)
        return
      }
      // Error mapping
      const messages: Record<string, string> = {
        wrong_code: body.attempts_remaining != null
          ? `Incorrect code. ${body.attempts_remaining} attempt${body.attempts_remaining === 1 ? '' : 's'} left.`
          : 'Incorrect code.',
        expired: 'This code expired. Tap "Resend" for a new one.',
        too_many_attempts: 'Too many wrong attempts. Tap "Resend" for a new code.',
        no_pending_code: 'No active code. Tap "Resend" to get a new one.',
        invalid_format: 'Enter all 6 digits.',
      }
      setError(messages[body.error || ''] || 'Could not verify. Try again.')
      setShake(true)
      setTimeout(() => setShake(false), 400)
      setDigits(Array(CODE_LENGTH).fill(''))
      setTimeout(() => inputsRef.current[0]?.focus(), 50)
    } catch {
      setError('Network error. Check your connection.')
    } finally {
      setSubmitting(false)
    }
  }, [getToken, onVerified, submitting, verified])

  const handleDigitChange = (idx: number, raw: string) => {
    // Strip non-digits
    const cleaned = raw.replace(/\D/g, '')
    if (!cleaned) {
      // User cleared a cell
      const next = [...digits]
      next[idx] = ''
      setDigits(next)
      return
    }
    // Paste of 6 digits in any cell fills all
    if (cleaned.length === CODE_LENGTH) {
      const arr = cleaned.slice(0, CODE_LENGTH).split('')
      setDigits(arr)
      inputsRef.current[CODE_LENGTH - 1]?.focus()
      void submit(arr.join(''))
      return
    }
    const ch = cleaned[cleaned.length - 1]
    const next = [...digits]
    next[idx] = ch
    setDigits(next)
    if (idx < CODE_LENGTH - 1) {
      inputsRef.current[idx + 1]?.focus()
    } else if (next.every(d => d)) {
      void submit(next.join(''))
    }
  }

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus()
    }
    if (e.key === 'Enter') {
      const code = digits.join('')
      if (code.length === CODE_LENGTH) void submit(code)
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const txt = e.clipboardData.getData('text').replace(/\D/g, '')
    if (txt.length === CODE_LENGTH) {
      e.preventDefault()
      const arr = txt.split('')
      setDigits(arr)
      inputsRef.current[CODE_LENGTH - 1]?.focus()
      void submit(arr.join(''))
    }
  }

  const resendLabel =
    resendStatus === 'sending' ? 'Sending…' :
    resendStatus === 'sent' ? 'Code sent!' :
    cooldown > 0 ? `Resend in ${cooldown}s` :
    'Resend code'
  const resendDisabled = cooldown > 0 || resendStatus === 'sending'

  return (
    <div style={styles.backdrop}>
      <style>{`
        @keyframes shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-6px)} 40%{transform:translateX(6px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(4px)} }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
      <div style={{ ...styles.card, ...(shake ? { animation: 'shake 0.4s' } : {}) }}>
        {verified ? (
          <div style={styles.successWrap}>
            <div style={styles.successCircle}>
              <Check size={28} color="#16a34a" strokeWidth={3} />
            </div>
            <h2 style={styles.title}>Email verified</h2>
            <p style={styles.subtitle}>Continuing to your dashboard…</p>
          </div>
        ) : (
          <>
            <h2 style={styles.title}>Check your email</h2>
            <p style={styles.subtitle}>
              We sent a 6-digit code to<br />
              <strong style={styles.email}>{email}</strong>
            </p>

            <div style={styles.digitsRow}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={el => { inputsRef.current[i] = el }}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={CODE_LENGTH}
                  value={d}
                  onChange={e => handleDigitChange(i, e.target.value)}
                  onKeyDown={e => handleKeyDown(i, e)}
                  onPaste={handlePaste}
                  disabled={submitting}
                  style={styles.digit}
                />
              ))}
            </div>

            {error && <div style={styles.errorText}>{error}</div>}
            {submitting && (
              <div style={styles.submittingRow}>
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Verifying…
              </div>
            )}

            <button
              type="button"
              onClick={() => void sendCode(false)}
              disabled={resendDisabled}
              style={{
                ...styles.resendBtn,
                ...(resendDisabled ? styles.resendBtnDisabled : {}),
                ...(resendStatus === 'sent' ? { color: '#16a34a' } : {}),
              }}
            >
              {resendLabel}
            </button>
            {resendStatus === 'error' && resendError && (
              <div style={styles.resendError}>{resendError}</div>
            )}

            {onSignOut && (
              <button type="button" onClick={onSignOut} style={styles.escape}>
                <X size={11} /> Wrong email? Sign out
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(6, 6, 12, 0.85)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
  },
  card: {
    width: '100%',
    maxWidth: 420,
    background: '#121218',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 20,
    padding: '36px 32px 28px',
    boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: 'var(--text-primary)',
    margin: '0 0 8px',
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: 14,
    color: 'var(--text-secondary)',
    lineHeight: 1.6,
    margin: '0 0 24px',
  },
  email: {
    color: 'var(--text-primary)',
    fontWeight: 600,
  },
  digitsRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 16,
  },
  digit: {
    width: 44,
    height: 54,
    fontSize: 24,
    fontWeight: 600,
    textAlign: 'center',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 10,
    background: '#1a1a24',
    color: 'var(--text-primary)',
    outline: 'none',
    fontFamily: 'inherit',
    fontVariantNumeric: 'tabular-nums',
  },
  errorText: {
    fontSize: 12,
    color: 'var(--danger, #ef4444)',
    marginBottom: 8,
    minHeight: 16,
  },
  submittingRow: {
    fontSize: 12,
    color: 'var(--text-tertiary)',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
  },
  resendBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--text-secondary)',
    fontSize: 13,
    cursor: 'pointer',
    padding: '8px 12px',
    fontFamily: 'inherit',
    marginTop: 8,
  },
  resendBtnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  resendError: {
    fontSize: 12,
    color: 'var(--danger, #ef4444)',
    marginTop: 4,
  },
  escape: {
    background: 'none',
    border: 'none',
    color: 'var(--text-tertiary)',
    fontSize: 11,
    cursor: 'pointer',
    padding: '12px 8px 0',
    fontFamily: 'inherit',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  },
  successWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 14,
    padding: '12px 0',
  },
  successCircle: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: 'rgba(22,163,74,0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}
